import { TextLink } from '@tableau/tableau-ui';
import * as React from 'react';
import { makeTestId } from '../utils/Declarative';
import { AnchorAttrs } from '../utils/HtmlAttrs';

export interface TextLinkProps extends AnchorAttrs {
  /**
   * The kind of TextLink you want to display. This affects the style of the link
   * @default TextLinkKind.Standalone
   */
  kind?: TextLinkKind;

  /** Test id to be attached to the anchor tag. Will be suffixed with '-TextLink'. */
  testId: string;
}

export enum TextLinkKind {
  /** Most commonly used within a table or list. Should not be surrounded by other text. */
  Standalone = 'standalone',
  /** For links which are embedded within a body of text. */
  Standard = 'standard',
  /** For use as secondary or tertiary navigation, such as "Manage Settings", which is infrequently accessed by a user. */
  Lightweight = 'lightweight',

  /** Same as above, but for use over dark backgrounds. */
  StandaloneDark = 'standalone-dark',
  /** Same as above, but for use over dark backgrounds. */
  StandardDark = 'standard-dark',
  /** Same as above, but for use over dark backgrounds. */
  LightweightDark = 'lightweight-dark',

  /** @deprecated use `Lightweight` */
  LowEmphasis = 'lightweight',
  /** @deprecated use `Lightweight-dark` */
  LowEmphasisDark = 'lightweight-dark',
}

/** Please see {@link http://design/guidelines/components/text-links#anatomy Design Site} for guidance on icon usage within a `TextLinkWidget` */
export const TextLinkWidget = React.forwardRef<HTMLAnchorElement, TextLinkProps>((props, ref) => {
  const { testId: testIdProp, ...textLinkProps } = props;
  return <TextLink data-tb-test-id={makeTestId(testIdProp, 'TextLink')} {...textLinkProps} ref={ref} />;
});
TextLinkWidget.displayName = 'TextLinkWidget';
